import { humanFilesize, isFile, isObj } from '@helpers/utils.js'
import isAlphanumeric from 'validator/es/lib/isAlphanumeric'
import isEmail from 'validator/es/lib/isEmail'
import i18n from '@plugins/localisation.js'
import isURL from 'validator/es/lib/isURL'

// Each validation rule will accept input and optional error message
export const required = ({ input, error } = {}) => {
  if (isObj(input) && input.file === null) {
    return error || i18n.global.t('validation_required')
  }

  return input === 1 ||
    input === '1' ||
    input === 0 ||
    input === '0' ||
    (input && input.length > 0) ||
    isObj(input) ||
    input
    ? true
    : error || i18n.global.t('validation_required')
}

export const requiredNotZero = ({ input, error } = {}) => {
  return (input === 1 ||
    input === '1' ||
    (input && input.length > 0) ||
    isObj(input) ||
    input) &&
    input !== 0 &&
    input !== '0'
    ? true
    : error || i18n.global.t('validation_required')
}

export const validateIf = (condition, validation, error) => {
  const fn = {
    temp: function ({ input }) {
      if (condition()) {
        return validation({ input, error })
      }
      return true
    },
  }['temp']

  fn.funcName = validation.name
  return fn
}

// used for required check checkboxes
export const checked = ({ input, error } = {}) => {
  return input === 1 || input === true
    ? true
    : error || i18n.global.t('validation_checked')
}

export const email = ({ input, error } = {}) => {
  if (!input) {
    return true
  }
  return input && isEmail(input)
    ? true
    : error || i18n.global.t('validation_email')
}

export const min = (number) => {
  return function min({ input, error } = {}) {
    if (!input) return true
    return input && input.length >= number
      ? true
      : error || i18n.global.t('validation_min', { min: number })
  }
}

export const max = (number) => {
  return function max({ input, error }) {
    if (!input) return true
    return input && input.length <= number
      ? true
      : error || i18n.global.t('validation_max', { max: number })
  }
}

export const url = ({ input, error } = {}) => {
  if (!input) return true

  return isURL(input) ? true : error || i18n.global.t('validation_url')
}

export const size = (len) => {
  return ({ input, error } = {}) => {
    if (!input) return true
    return input && input.length === len
      ? true
      : error || i18n.global.t('validation_size', { size: len })
  }
}

export const alphaNum = ({ input, error } = {}) => {
  if (!input) return true

  return input && isAlphanumeric(input)
    ? true
    : error || i18n.global.t('validation_alpha_num')
}

export const regex = (reg, error) => {
  return function regex({ input } = {}) {
    if (!input) return true
    return reg.test(input) ? true : error
  }
}

export const image = ({ input, error } = {}) => {
  if (!input?.file) return true

  if (isFile(input.file)) {
    const ext = input.file.name.split('.')

    if (['jpg', 'jpeg', 'png'].includes(ext[ext.length - 1].toLowerCase())) {
      return true
    }
  }

  return error || i18n.global.t('validation_image')
}

export const dimensions = ({ minWidth, minHeight, error } = {}) => {
  return function dimensions({ input } = {}) {
    try {
      const errorMessage = []

      if (input && image({ input }) === true) {
        if (minWidth && input.width && input.width < minWidth) {
          errorMessage.push(`Image width must be at least ${minWidth}px`)
        }
        if (minHeight && input.height && input.height < minHeight) {
          errorMessage.push(
            `${
              errorMessage.length ? 'and' : 'Image'
            } height must be at least ${minHeight}px`
          )
        }
      }

      return errorMessage.length ? error || errorMessage.join(' ') : true
    } catch (e) {
      console.error(e)
    }
  }
}

export const maxFileSize = (size, error) => {
  return function maxFileSize({ input }) {
    if (!input?.file || parseInt(input.file.size) < parseInt(size)) {
      return true
    }

    return (
      error ||
      i18n.global.t('validation_filesize', { size: humanFilesize(size) })
    )
  }
}

export const isZipFile = ({ input, error } = {}) => {
  if (input && isFile(input.file)) {
    const ext = input.file.name.split('.')

    if (ext[ext.length - 1] === 'zip') {
      return true
    }
  }

  return error ? error : i18n.global.t('validation_zip')
}

export const youtubeLink = ({ input, error } = {}) => {
  if (!input) return true

  const regex =
    /http(?:s?):\/\/(?:www\.)?youtu(?:be\.com\/watch\?v=|\.be\/)([\w\-_]*)(&(amp;)?[\w?=]*)?/i

  return regex.test(input) ? true : error || i18n.global.t('validation_youtube')
}

export const sketchfabLink = ({ input, error } = {}) => {
  if (!input) return true

  const regex =
    /http(?:s?):\/\/(?:www\.)?sketchfab\.com\/(?:3d-)?models\/(?:[a-z0-9-]*)([a-z0-9]{32})/

  return regex.test(input)
    ? true
    : error || i18n.global.t('validation_sketchfab')
}
